import { Skeleton, Tooltip } from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import SpecialistApi from "../../../../api/specialistsApi";
import { ReactComponent as EditIcon } from "../../../../assets/Certificates/edit.svg";
import BlurImageEditor from "../../../../components/BlurImageEditor/BlurImageEditor";
import BlurPdfEditor from "../../../../components/BlurPdfEditor/BlurPdfEditor";
import ModalWindow from "../../../../components/ModalWindow/ModalWindow";
import StandartButton from "../../../../components/StandartButton/StandartButton";
import { ContextProvider } from "../../../../contextProvider";
import {
  AppreciationCertificate,
  SpecialistData,
} from "../../../../types/TypeSpecialist";
import { formatDateAndTime } from "../../../../utils/dateActions";
import { getPdfThumbnail } from "../../../../utils/filesAction";
import Notify, { SuccesNotify } from "../../../../utils/toaster";
import UpdateCertificatesModal from "../AddCertificatesModal/UpdateCertificatesModal";
import { CertificatesData } from "../EducationSection";
import s from "../EducationSection.module.css";

type ApppreciationCardProps = {
  appreciationData: AppreciationCertificate;
  previewFileHandler: (link: string, type: string) => void;
  specialistId: string;
  specialistData: SpecialistData;
  setCertificatesData: Dispatch<SetStateAction<CertificatesData>>;
};

const ApppreciationCard = ({
  appreciationData,
  previewFileHandler,
  specialistId,
  specialistData,
  setCertificatesData,
}: ApppreciationCardProps) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const [certificateThumbnail, setCertificateThumbnail] = useState<
    string | undefined
  >();
  const certificateFileLink = appreciationData.file?.link;
  const certificateFileType = appreciationData.file?.type ?? "";
  const [thumbnailLoader, setThumbnailLoader] = useState(false);
  const [imageEditorModal, setImageEditorModal] = useState(false);
  const [certificateLink, setCertificateLink] = useState<string | undefined>(
    certificateFileLink
  );
  const [educationModal, setEducationModal] = useState(false);

  useEffect(() => {
    setCertificateLink(certificateFileLink);
  }, [certificateFileLink]);

  useEffect(() => {
    if (!certificateLink) return;
    const loadThumbnail = async () => {
      if (certificateFileType.toLowerCase() === "pdf") {
        setThumbnailLoader(true);
        const thumbnail = await getPdfThumbnail(certificateLink);
        setCertificateThumbnail(thumbnail);
        setThumbnailLoader(false);
      } else {
        setCertificateThumbnail(certificateLink);
      }
    };

    loadThumbnail();
  }, [certificateLink, certificateFileType]);

  const updateCertificateFile = async (newFile: File) => {
    if (!token) return;
    const payload = {
      file: newFile,
      userId: userData!._id,
      specialistId: specialistId,
      updateData: { _id: appreciationData._id },
    };
    const response = await SpecialistApi.updateAppreciationCertificate(
      token,
      payload
    );
    if (!response.status && response.message) {
      Notify(response.message);
    }
    if (response.status && response.certificate) {
      setImageEditorModal(false);
      setCertificateLink(response.certificate.file?.link);
      SuccesNotify("Certificate has been updated");
    }
  };

  return (
    <div className={s.certificateElement}>
      {certificateThumbnail && !thumbnailLoader ? (
        <Tooltip arrow title={"Show certificate"}>
          <div
            onClick={() =>
              previewFileHandler(
                certificateFileLink ?? "",
                certificateFileType
              )
            }
            className={s.showCertificateBtn}
          >
            <img
              src={certificateThumbnail}
              alt="PDF Thumbnail"
              className={s.thumbnailCertificate}
            />
          </div>
        </Tooltip>
      ) : (
        <Skeleton variant="rounded" width={"100%"} height={200} />
      )}
      <div className={s.mainInfoCertificateBlock}>
        <div className={s.headCertificateBlock}>
          <span>{appreciationData.organization}</span>
        </div>
        <div className={s.headCertificateBlock}>
          <span>{appreciationData.receivedFor}</span>
        </div>
        <div className={s.bottomCertificateBlock}>
          <span>
            {
              formatDateAndTime(
                new Date(appreciationData.receiveDate),
                userData?.selectedLanguage ?? "en"
              ).formattedDate
            }
          </span>
        </div>
        <div className={s.actionBlock}>
          {certificateFileLink ? (
            <StandartButton
              action={() => setImageEditorModal(true)}
              buttonTitle="Blur surname"
              width="calc(100% - 62px)"
            />
          ) : (
            <div></div>
          )}
          <div
            role="button"
            className={s.editBtn}
            onClick={() => setEducationModal(true)}
          >
            <EditIcon />
          </div>
        </div>
      </div>
      {certificateFileLink && (
        <ModalWindow
          isOpen={imageEditorModal}
          setIsOpen={setImageEditorModal}
          width="fit-content"
        >
          {certificateFileType !== "pdf" ? (
            <BlurImageEditor
              imageUrl={certificateFileLink}
              saveImage={updateCertificateFile}
            />
          ) : (
            <BlurPdfEditor
              pdfUrl={certificateFileLink}
              savePdf={updateCertificateFile}
            />
          )}
        </ModalWindow>
      )}
      <UpdateCertificatesModal
        certificateType="appreciation"
        modalOpen={educationModal}
        setModalOpen={setEducationModal}
        specialistId={specialistData?._id!}
        setNewCertificate={setCertificatesData}
        mainSpec={specialistData?.mainSpecializations[0]._id!}
        appreciationToUpdate={appreciationData}
      />
    </div>
  );
};

export default ApppreciationCard;
